import React, { lazy, Suspense, useCallback, useState } from 'react';

import { Alert, Box, CircularProgress } from '@mui/material';

import { SECRET_TYPES } from '@/types';

import ActionButtons from '@/components/secret/SecretDisplay/components/ActionButtons.tsx';

import { styles } from './secretDisplayStyles.ts';

import type { BaseSecretTypeProps, CredentialsData, DecryptedSecret, SecretDisplayProps, SecretType } from '@/types';

// Lazy load all secret type components
const CredentialsType = lazy(() => import('./components/CredentialsType'));
const MessageType = lazy(() => import('./components/MessageType'));
const PasswordType = lazy(() => import('./components/PasswordType'));
const CustomType = lazy(() => import('./components/CustomType'));
const FileType = lazy(() => import('./components/FileType'));
const QRType = lazy(() => import('./components/QrType'));

// Loading fallback component
const LoadingFallback = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={32} />
    </Box>
);

// Component map for dynamic rendering
const SECRET_COMPONENT_MAP: Record<SecretType, React.LazyExoticComponent<React.ComponentType<any>>> = {
    [SECRET_TYPES.CREDENTIALS]: CredentialsType,
    [SECRET_TYPES.MESSAGE]: MessageType,
    [SECRET_TYPES.PASSWORD]: PasswordType,
    [SECRET_TYPES.CUSTOM]: CustomType,
    [SECRET_TYPES.FILE]: FileType,
    [SECRET_TYPES.QR]: QRType,
};

/**
 * Get copy text based on secret type
 */
const getCopyText = (secret: DecryptedSecret): string => {
    switch (secret.type) {
        case SECRET_TYPES.CREDENTIALS: {
            const creds = secret.content as CredentialsData;
            const parts = [`Username: ${creds.user}`, `Password: ${creds.password}`];

            if (creds.hostname) {
                parts.push(`URL: ${creds.hostname}`);
            }

            if (creds.secret) {
                parts.push(`2FA Secret: ${creds.secret}`);
            }

            return parts.join('\n');
        }

        case SECRET_TYPES.CUSTOM: {
            if (typeof secret.content === 'object' && secret.content !== null) {
                return JSON.stringify(secret.content, null, 2);
            }
            return String(secret.content);
        }

        default:
            return typeof secret.content === 'string' ? secret.content : '';
    }
};

/**
 * SecretDisplay Component
 *
 * Dynamically renders the appropriate component based on secret type.
 * All type-specific components are lazy-loaded for better code splitting.
 */
export const SecretDisplay = ({ secret, onDownloadFile, onDecryptFile }: SecretDisplayProps) => {
    const [isHidden, setIsHidden] = useState(false);

    const handleToggleVisibility = useCallback(() => {
        setIsHidden((prev) => !prev);
    }, []);

    const handleCopyAll = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(getCopyText(secret));
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    }, [secret]);

    // Get the component for this secret type
    const SecretComponent = SECRET_COMPONENT_MAP[secret.type];

    // Fallback for unknown types
    if (!SecretComponent) {
        console.warn(`Unknown secret type: ${secret.type}`);

        return (
            <Box sx={styles.container}>
                <Box sx={{ color: 'error.main' }}>Unknown secret type: {secret.type}</Box>
            </Box>
        );
    }

    // Build props based on secret type
    const componentProps: BaseSecretTypeProps & {
        onDownloadFile?: () => Promise<void>;
        onDecryptFile?: () => Promise<ArrayBuffer | null>;
    } = {
        secret,
        isHidden,
        ...(secret.type === SECRET_TYPES.FILE && { onDownloadFile, onDecryptFile }),
    };

    return (
        <Suspense fallback={<LoadingFallback />}>
            <Box sx={styles.revealWrapper}>
                <Alert severity="success" icon={false}>
                    Your secret has been revealed successfully.
                </Alert>

                {secret.publicDescription && <Alert severity="info">Description: {secret.publicDescription}</Alert>}

                {/* Neither button means anything for a file. Hide toggled a state FileType never
                    read, so it did nothing at all; and a file's `content` is the URL its bytes are
                    fetched from, so Copy put that on the clipboard instead of the secret. What a
                    file offers is playing it and saving it, which FileType has. */}
                {secret.type !== SECRET_TYPES.FILE && (
                    <ActionButtons
                        isHidden={isHidden}
                        handleToggleVisibility={handleToggleVisibility}
                        handleCopyAll={handleCopyAll}
                    />
                )}

                <SecretComponent {...componentProps} />
            </Box>
        </Suspense>
    );
};

export default SecretDisplay;
